import {
  Column,
  detailsColumns,
  DetailsSortVariant,
  PoolStats,
  ShareClassStats,
  StakeholderStats,
  StakeholderTypeStats,
} from '../../types/snapshot.types';
import { StakeholderType } from '../../types/stakeholder.types';

export enum SharedCapTableExpireInterval {
  IMMEDIATELY = 'immediately',
  ONE_DAY = 'one-day',
  ONE_MONTH = 'one-month',
  SEVEN_DAYS = 'seven-days',
  THREE_DAYS = 'three-days',
  TWO_WEEKS = 'two-weeks',
}

export const MAX_ACTIVE_SHARED_CAP_TABLE_LINKS = 5;

export const SHARED_CAP_TABLE_NOTE_MAX_LENGTH = 300;

export const SHARED_CAP_TABLE_MIN_PASSWORD_LENGTH = 8;

/**
 * Columns that never get a toggle: NAME is the row identity, governed by the Hide Stakeholder
 * names option instead, and TYPE is already shown as a group header rather than a column.
 */
const ALWAYS_SHARED_COLUMNS: Column[] = [Column.NAME, Column.TYPE];

/** Derived from the cap table's own column list, so the two cannot drift apart. */
const toShareableColumns = (variant: DetailsSortVariant): Column[] =>
  detailsColumns[variant].filter(
    (column) => !ALWAYS_SHARED_COLUMNS.includes(column as Column),
  ) as Column[];

export const SHAREABLE_COLUMNS_BY_STAKEHOLDER: Column[] = toShareableColumns(
  DetailsSortVariant.BY_STAKEHOLDER,
);

export const SHAREABLE_COLUMNS_BY_SHARE_CLASS: Column[] = toShareableColumns(
  DetailsSortVariant.BY_SHARE_CLASS,
);

export const SHAREABLE_COLUMNS: Record<DetailsSortVariant, Column[]> = {
  [DetailsSortVariant.BY_SHARE_CLASS]: SHAREABLE_COLUMNS_BY_SHARE_CLASS,
  [DetailsSortVariant.BY_STAKEHOLDER]: SHAREABLE_COLUMNS_BY_STAKEHOLDER,
};

export type SharedCapTableViewOptions = {
  hideOverallStatus: boolean;
  hideOwnershipChart: boolean;
  hideStakeholderNames: boolean;
};

export type SharedCapTableSettings = {
  allowExport: boolean;
  columns: Record<DetailsSortVariant, Column[]>;
  defaultView: DetailsSortVariant;
  viewOptions: SharedCapTableViewOptions;
};

export type SharedCapTableLink = {
  createdAt: string;
  expiresAt: null | string;
  hasPassword: boolean;
  id: string;
  linkId: string;
  note: null | string;
  settings: SharedCapTableSettings;
};

export type CreateSharedCapTableLinkDto = {
  expireInterval?: null | SharedCapTableExpireInterval;
  note?: null | string;
  password?: null | string;
  settings: SharedCapTableSettings;
};

export type PatchSharedCapTableLinkDto = {
  expireInterval?: null | SharedCapTableExpireInterval;
  note?: null | string;
  /** Omit to keep the stored password; `null` removes protection. */
  password?: null | string;
  settings?: SharedCapTableSettings;
};

export type PublicCapTableOverview = {
  dilutedShares: number;
  investorsCount: number;
  issuedShares: number;
  lifetimeInvestmentEquity: number;
  poolDilutedShares: number;
  poolGrantedShares: number;
  postMoneyValuation: number;
};

export type PublicOwnershipSlice = {
  issuedPercentage: number;
  /** `Pool` when the company counts its available pool as issued — it is not a stakeholder type. */
  type: 'Pool' | StakeholderType;
  votingPercentage: number;
};

/**
 * Mirrors the sanitised payload from the backend mapper, in the same shape as the cap table's own
 * `stats` so its row components render it unchanged: withheld columns arrive zeroed and ids are
 * positional handles. The exception is the structural set, which the renderer lays rows out with.
 */
export type PublicCapTablePayload = {
  allowExport: boolean;
  columns: Record<DetailsSortVariant, Column[]>;
  /** Signed server-side: the viewer has no session to presign a private bucket object with. */
  companyLogoUrl: null | string;
  companyName: null | string;
  defaultShareClassId: string;
  defaultView: DetailsSortVariant;
  /** Row-layout totals for the equity-pool rows; separate from the Overall Status block. */
  dilutedShares: number;
  /** Rows arrive as `Stakeholder 1..N`; the page prints them unnumbered. */
  hideStakeholderNames: boolean;
  /** Counted server-side by the rule the authorised Overall Status uses, before columns are cut. */
  stakeholdersCount: number;
  issuedShares: number;
  overview: null | PublicCapTableOverview;
  ownership: null | PublicOwnershipSlice[];
  pools: PoolStats[];
  shareClasses: ShareClassStats[];
  stakeholders: PublicStakeholderStats[];
  stakeholdersTypes: StakeholderTypeStats[];
  updatedAt: string;
};

/** `type` is structural — it drives the category grouping, but is not a selectable column. */
export type PublicStakeholderStats = StakeholderStats;

export const defaultSharedCapTableSettings = (): SharedCapTableSettings => ({
  allowExport: false,
  columns: {
    [DetailsSortVariant.BY_SHARE_CLASS]: [...SHAREABLE_COLUMNS_BY_SHARE_CLASS],
    [DetailsSortVariant.BY_STAKEHOLDER]: [...SHAREABLE_COLUMNS_BY_STAKEHOLDER],
  },
  defaultView: DetailsSortVariant.BY_STAKEHOLDER,
  viewOptions: {
    hideOverallStatus: false,
    hideOwnershipChart: false,
    hideStakeholderNames: false,
  },
});
